import { Fragment, useMemo, useState } from 'react';
import {
  crmMembers,
  crmMyDay,
  crmProspects,
  salesKitRowsForPrep,
  salesLogOutcome,
  salesPrep,
  type MeetingPrepResponse,
  type SalesKitSectionRow,
} from '@aithora/shared';
import { useSession } from '../../../session/SessionContext';
import { useQuery } from '../../../useQuery';
import { ErrorNote } from '../../../ErrorNote';
import { useSales } from '../context';
import { ck, hov, sx } from '../dc';
import { ago, cap, disabledWrite, firstName, mailHref, title } from './util';
import { BuildLabel, BuildTagged } from '../../../ui/buildLabels';

const NK = 'Not known · ask';
const NO_CONTACT = 'No named contact yet · ask for the Administrator and the DON';
const ALWAYS_KEY = 'dq.the_one_to_always_ask';

/** A CMS signal as the design's "Why now" line (its flags: "3★ staffing", "1 recent penalty"). */
function signalText(key: string, value: unknown): string | null {
  const n = Number(value);
  switch (key) {
    case 'overall_stars':
      return `${n}★ overall`;
    case 'staffing_stars':
      return `${n}★ staffing`;
    case 'health_inspection_stars':
      return `${n}★ inspections`;
    case 'penalties':
      return `${n} recent ${n === 1 ? 'penalty' : 'penalties'}`;
    case 'fines_dollars':
      return `$${Math.round(n).toLocaleString('en-US')} in fines`;
    case 'last_standard_survey':
      return `Last standard survey ${String(value)}`;
    case 'operating_margin':
      return n < 0 ? 'negative operating margin' : `${n}% operating margin`;
    default:
      return null;
  }
}
const sourceLabel = (source: string, asOf: string | null) => {
  const s = source.startsWith('CMS') ? 'CMS' : source;
  const y = asOf ? (asOf.match(/(\d{4})/) ?? [])[1] : null;
  return y ? `${s} · ${y}` : s;
};

/**
 * Meeting prep (design mpOpen, T6681-6741; mp* values L20941-21011). Opened from the CRM
 * deal panel, the Territory facility panel, Outcomes and the Sales Kit. With no facility it
 * asks who you are meeting (open deals first, then top prospects); with one it is built on
 * every open from GET /sales/prep/:facilityId, never stored. The agenda, pushback and proof
 * copy live in the Sales Kit: the server returns keys, the kit rows carry the words.
 */
export function MeetingPrep({ facilityId, onClose, onPick }: { facilityId: string | null; onClose: () => void; onPick: (facilityId: string) => void }) {
  const { client, session } = useSession();
  const sales = useSales();
  const [out, setOut] = useState('');
  const [next, setNext] = useState('');
  const [date, setDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<unknown>(null);


  const picking = !facilityId;
  const day = useQuery((signal) => crmMyDay(client, { query: { scope: 'team' }, signal }), [client, session?.token], { enabled: picking });
  const pros = useQuery((signal) => crmProspects(client, { query: { limit: 10 }, signal }), [client, session?.token], { enabled: picking });
  const prep = useQuery((signal) => salesPrep(client, { params: { facilityId: facilityId! }, signal }), [client, session?.token, facilityId], { enabled: !picking });
  const kit = useQuery((signal) => salesKitRowsForPrep(client, { signal }), [client, session?.token]);
  const members = useQuery((signal) => crmMembers(client, { signal }), [client, session?.token]);

  const byKey = useMemo(() => {
    const m = new Map<string, SalesKitSectionRow[]>();
    for (const r of kit.data?.sections ?? []) {
      if (!r.key) continue;
      m.set(r.key, [...(m.get(r.key) ?? []), r]);
    }
    return m;
  }, [kit.data]);
  const linkOf = (doc: string) => (kit.data?.sections ?? []).find((r) => r.doc === doc && r.link)?.link ?? '';

  const pool = useMemo(() => {
    const seen = new Set<string>();
    const rows: Array<{ id: string; n: string; meta: string }> = [];
    for (const d of day.data?.today ?? []) {
      if (!d.facility_id || seen.has(d.facility_id)) continue;
      seen.add(d.facility_id);
      rows.push({ id: d.facility_id, n: title(d.facility_name ?? d.name), meta: cap(d.stage) });
    }
    for (const p of pros.data?.prospects ?? []) {
      if (seen.has(p.facilityId)) continue;
      seen.add(p.facilityId);
      rows.push({ id: p.facilityId, n: title(p.name), meta: 'Not started' });
    }
    return rows;
  }, [day.data, pros.data]);

  const p: MeetingPrepResponse | null = prep.data;
  const stage = p?.stage ?? null;
  const atLead = !stage || stage.toLowerCase() === 'lead';
  const f = p?.facts;
  const profile = f ? [f.type, f.state, f.beds != null ? `${f.beds} beds` : null, f.tier ? `Tier ${f.tier}` : null].filter(Boolean).join(' · ') : '';
  const facts: Array<[string, string]> = f
    ? [
        ['Facility', profile || NK],
        ['Ownership', p!.flags.pe_backed ? 'PE-backed · corporate signs off' : f.ownership ?? NK],
        ['EHR', f.ehr ?? NK],
        ['Contact', f.contact ?? NO_CONTACT],
      ]
    : [];
  const why: Array<{ t: string; src: string }> = [];
  for (const s of p?.cms_signals ?? []) {
    const t = signalText(s.key, s.value);
    if (t) why.push({ t, src: sourceLabel(s.source, s.as_of) });
  }
  if (p?.priority) {
    why.push({ t: `Urgency ${p.priority.urgency}`, src: 'MODELED' });
    why.push({ t: `Ease ${p.priority.ease}`, src: 'MODELED' });
    why.push({ t: `Strategic ${p.priority.strategic}`, src: 'MODELED' });
  }
  const agenda = (p?.agenda.keys ?? []).flatMap((key) => (byKey.get(key) ?? []).map((r) => ({ t: key === ALWAYS_KEY ? `${r.text} (always ask)` : r.text })));
  const push = (p?.pushback.keys ?? []).flatMap((key) => (byKey.get(key) ?? []).map((r) => ({ q: r.label ?? r.section, a: r.text })));
  const demo = p ? (byKey.get(p.proof.key) ?? []).map((r) => ({ t: r.text })) : [];
  const last = (p?.last_activities ?? []).map((a) => ({
    t: cap(a.kind) + ': ' + (a.phi_suppressed ? 'Not logged: may contain health information' : a.subject ?? cap(a.kind)),
    when: (a.at ? ago(a.at) : '') + ' · ' + firstName(members.data?.members, a.owner_id),
  }));
  const how = linkOf('How It Works');
  const faq = linkOf('Sales FAQ');
  const nameT = f ? title(f.name) : '';

  const copy = (t: string) => () => {
    try {
      void navigator.clipboard?.writeText(t);
    } catch {
      /* the clipboard may be refused; the link is still on screen in the Sales Kit */
    }

  };
  const save = async () => {
    if (!facilityId || !out.trim() || !sales.canWrite || saving) return;
    setSaving(true);
    setSaveErr(null);
    try {
      await salesLogOutcome(client, { params: { facilityId }, body: { outcome: out.trim(), nextStep: next.trim() || undefined, nextMeetingAt: date || null } });
      setOut('');
      setNext('');
      setDate('');
      prep.reload();
    } catch (e) {
      setSaveErr(e);
    } finally {
      setSaving(false);
    }
  };

  const v = {
    mpHasFor: !picking && !!p,
    mpPicking: picking,
    mpName: nameT,
    mpStage: stage ? cap(stage) : 'Not a deal yet',
    mpMeet: p?.deal?.next_meeting_at ? 'Meeting ' + p.deal.next_meeting_at.slice(0, 10) : 'No meeting booked',
    mpFacts: facts.map(([k0, v0]) => ({
      k: k0,
      v: v0,
      vStyle: 'font-size:16px;line-height:1.4;color:' + (v0 === NK || v0.indexOf('No named') === 0 ? '#8A5A00' : '#22272E') + ';font-weight:' + (v0 === NK ? '700' : '500') + ';',
    })),
    mpWhy: why,
    mpWhyNone: why.length === 0,
    mpAmt: p?.open_dollars ? '$' + Math.round(p.open_dollars.per_year / 1000) + 'k/yr open' : 'Not modeled for this facility',
    mpAgenda: agenda,
    mpAgendaHead: atLead ? 'Questions to ask them' : 'Agenda at ' + cap(stage),
    mpPush: push,
    mpLast: last,
    mpLastNone: last.length === 0,
    mpDemo: demo,
    mpSaveNote: p?.deal ? 'Files to the deal history with who and when.' : 'Saving starts a deal at Lead and files this to it.',
    mpFollow: mailHref('Following up · Aithora', (next ? 'Next step: ' + next + (date ? ' by ' + date : '') + '\n\n' : '') + 'How it works: ' + how),
  };
  const saveStyle =
    'display:inline-flex;align-items:center;height:30px;padding:0 12px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#fff;background:#2F5C97;' +
    (sales.canWrite ? '' : disabledWrite);
  const leave = 'display:inline-flex;align-items:center;height:28px;padding:0 10px;border-radius:7px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;color:#22272E;border:1px solid rgba(34,39,46,.2);';
  const loadErr = prep.error ?? (picking ? day.error ?? pros.error : null);

  return (
    <div data-screen-label="Meeting prep" data-testid="meeting-prep" style={sx('position:fixed;inset:0;z-index:80;display:flex;align-items:center;justify-content:center;padding:20px;box-sizing:border-box;')}>
      <a href="#" onClick={ck(onClose)} aria-label="Close" style={sx('position:absolute;inset:0;background:rgba(34,39,46,.35);')} />{' '}
      <div role="dialog" aria-label="Meeting prep" style={sx('position:relative;width:1040px;max-width:100%;max-height:100%;overflow-y:auto;box-sizing:border-box;background:#F7F3EA;border-radius:12px;box-shadow:0 20px 60px rgba(34,39,46,.3);padding:16px 18px;display:flex;flex-direction:column;gap:12px;animation:riseIn .3s cubic-bezier(.22,.61,.36,1);')}>
        <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:4px;')}>
          <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Meeting prep</span>{' '}
          {v.mpHasFor && (
            <>
              <span style={sx('font-size:17px;font-weight:800;color:#22272E;')}>
                <span>{v.mpName}</span>
              </span>
              <span style={sx('font-size:14px;font-weight:800;letter-spacing:.07em;text-transform:uppercase;border-radius:4px;padding:2px 6px;color:#264C7E;background:rgba(79,138,214,.14);')}>
                <span>{v.mpStage}</span>
              </span>
              <span style={sx('font-size:16px;color:#54606C;')}>
                <span>{v.mpMeet}</span> &#183; built from live data just now
              </span>
            </>
          )}{' '}
          <span style={sx('flex:1;')} />{' '}
          <a href="#" onClick={ck(onClose)} aria-label="Close meeting prep" style={sx('display:inline-flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:8px;border:1px solid rgba(34,39,46,.18);font-size:16px;color:#22272E;text-decoration:none;')}>
            &#10005;
          </a>
        </div>{' '}
        {loadErr && <ErrorNote error={loadErr} onRetry={() => (picking ? (day.reload(), pros.reload()) : prep.reload())} compact />}
        {v.mpPicking && (
          <>
            <div style={sx('font-size:16px;color:#54606C;')}>Who are you meeting? Open deals first, then top prospects.</div>{' '}
            <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(230px,1fr));gap:4px;')}>
              {pool.map((x) => (
                <Fragment key={x.id}>
                  <a href="#" onClick={ck(() => onPick(x.id))} style={sx('display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:8px;text-decoration:none;color:#22272E;')} className={hov('background:rgba(79,138,214,.08);')}>
                    <span style={sx('font-size:16px;font-weight:600;min-width:0;flex:1 1 auto;overflow-wrap:break-word;')}>
                      <span>{x.n}</span>
                    </span>
                    <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>
                      <span>{x.meta}</span>
                    </span>
                  </a>
                </Fragment>
              ))}
            </div>
          </>
        )}{' '}
        {v.mpHasFor && (
          <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:14px 18px;')}>
            <div style={sx('display:flex;flex-direction:column;gap:7px;min-width:0;')}>
              <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>1 · Who they are</div>{' '}
              {v.mpFacts.map((x, i) => (
                <Fragment key={i}>
                  <div style={sx('display:grid;grid-template-columns:78px minmax(0,1fr);gap:8px;')}>
                    <span style={sx('font-size:14px;color:#54606C;')}>
                      <span>{x.k}</span>
                    </span>
                    <span style={sx(x.vStyle)}>
                      <span>{x.v}</span>
                    </span>
                  </div>
                </Fragment>
              ))}{' '}
              <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>2 · Why now</div>{' '}
              {v.mpWhyNone && <div style={sx('font-size:16px;color:#54606C;')}>No public signals on file for this facility.</div>}{' '}
              <div style={sx('display:flex;flex-direction:column;gap:4px;')}>
                {v.mpWhy.map((w, i) => (
                  <Fragment key={i}>
                    <div style={sx('display:flex;align-items:center;gap:7px;')}>
                      <span style={sx('font-size:16px;color:#22272E;flex:1 1 auto;min-width:0;')}>
                        <span>{w.t}</span>
                      </span>
                      <BuildTagged tag={w.src}>
                      <span style={sx('font-size:14px;font-weight:800;letter-spacing:.07em;color:#54606C;background:rgba(34,39,46,.07);border-radius:3px;padding:1px 4px;white-space:nowrap;')}>
                        <span>{w.src}</span>
                      </span>
                      </BuildTagged>
                    </div>
                  </Fragment>
                ))}
              </div>{' '}
              <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>3 · The money</div>{' '}
              <div style={sx('display:flex;align-items:center;gap:7px;')}>
                <span style={sx('font-size:18px;font-weight:800;font-variant-numeric:tabular-nums;color:#22272E;')}>
                  <span>{v.mpAmt}</span>
                </span>
                {p?.open_dollars?.modeled && (
                  <BuildLabel><span style={sx('font-size:14px;font-weight:800;letter-spacing:.07em;color:#264C7E;background:rgba(47,92,151,.12);border-radius:3px;padding:1px 4px;')}>MODELED</span></BuildLabel>
                )}
              </div>{' '}
              <div style={sx('font-size:14px;color:#54606C;line-height:1.45;')}>
                Billed by their own enrolled practitioner, not by Aithora. The pilot replaces this with their real numbers.
              </div>
            </div>{' '}
            <div style={sx('display:flex;flex-direction:column;gap:7px;min-width:0;')}>
              <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>4 · Where we left off</div>{' '}
              {v.mpLastNone && <div style={sx('font-size:16px;color:#54606C;')}>First conversation. Nothing on file yet.</div>}{' '}
              {v.mpLast.map((l, i) => (
                <Fragment key={i}>
                  <div style={sx('display:flex;flex-direction:column;gap:1px;')}>
                    <span style={sx('font-size:16px;color:#22272E;')}>
                      <span>{l.t}</span>
                    </span>
                    <span style={sx('font-size:14px;color:#54606C;')}>
                      <span>{l.when}</span>
                    </span>
                  </div>
                </Fragment>
              ))}{' '}
              <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>
                5 &#183; <span>{v.mpAgendaHead}</span>
              </div>{' '}
              {v.mpAgenda.map((a, i) => (
                <Fragment key={i}>
                  <div style={sx('display:flex;gap:7px;font-size:16px;color:#22272E;line-height:1.45;')}>
                    <span style={sx('color:#2F5C97;font-weight:800;')}>&#8226;</span>
                    <span>
                      <span>{a.t}</span>
                    </span>
                  </div>
                </Fragment>
              ))}{' '}
              <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>6 · Likely pushback</div>{' '}
              {v.mpPush.map((x, i) => (
                <Fragment key={i}>
                  <div style={sx('display:flex;flex-direction:column;gap:1px;')}>
                    <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>
                      &ldquo;<span>{x.q}</span>&rdquo;
                    </span>
                    <span style={sx('font-size:16px;color:#3C454F;line-height:1.45;')}>
                      <span>{x.a}</span>
                    </span>
                  </div>
                </Fragment>
              ))}
            </div>{' '}
            <div style={sx('display:flex;flex-direction:column;gap:7px;min-width:0;')}>
              <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>7 · Proof to bring</div>{' '}
              <span style={sx('align-self:flex-start;font-size:14px;font-weight:800;letter-spacing:.07em;color:#8A5A00;background:rgba(165,86,26,.13);border-radius:3px;padding:2px 5px;')}>
                {(p?.proof.label ?? '').toUpperCase()}
              </span>{' '}
              {v.mpDemo.map((d, i) => (
                <Fragment key={i}>
                  <div style={sx('font-size:16px;color:#22272E;')}>
                    <span>{d.t}</span>
                  </div>
                </Fragment>
              ))}{' '}
              <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>What to leave behind</div>{' '}
              <div style={sx('display:flex;gap:5px;flex-wrap:wrap;')}>
                <a
                  href="#"
                  onClick={ck(() => {
                    sales.setTab('Sales Kit', { kitMode: 'lib', slDoc: 'Product One-Pager', kitPzQuery: nameT });
                    onClose();
                  })}
                  style={sx(leave)}
                >
                  One-pager for them
                </a>{' '}
                <a href="#" onClick={ck(copy(how))} aria-disabled={!how || undefined} title={how ? undefined : 'The hosted How It Works link is not in the Sales Kit yet.'} style={sx(leave)}>
                  Copy How It Works link
                </a>{' '}
                <a href="#" onClick={ck(copy(faq))} aria-disabled={!faq || undefined} title={faq ? undefined : 'The hosted Sales FAQ link is not in the Sales Kit yet.'} style={sx(leave)}>
                  Copy FAQ link
                </a>
              </div>{' '}
              <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>8 · After the meeting</div>{' '}
              <input type="text" aria-label="What happened?" value={out} onChange={(e) => setOut(e.target.value)} placeholder="What happened?" style={sx('height:30px;box-sizing:border-box;padding:0 10px;border-radius:7px;border:1px solid rgba(34,39,46,.2);background:#FCFAF4;font:inherit;font-size:16px;color:#22272E;')} />{' '}
              <div style={sx('display:flex;gap:6px;')}>
                <input type="text" aria-label="Next step" value={next} onChange={(e) => setNext(e.target.value)} placeholder="Next step" style={sx('flex:1 1 auto;min-width:0;height:30px;box-sizing:border-box;padding:0 10px;border-radius:7px;border:1px solid rgba(34,39,46,.2);background:#FCFAF4;font:inherit;font-size:16px;color:#22272E;')} />
                <input type="date" aria-label="Next meeting date" value={date} onChange={(e) => setDate(e.target.value)} style={sx('height:30px;box-sizing:border-box;padding:0 8px;border-radius:7px;border:1px solid rgba(34,39,46,.2);background:#FCFAF4;font:inherit;font-size:16px;color:#22272E;')} />
              </div>{' '}
              <div style={sx('display:flex;gap:5px;flex-wrap:wrap;')}>
                <a href="#" onClick={ck(save)} aria-disabled={!sales.canWrite || saving || undefined} title={sales.canWrite ? undefined : sales.writeReason ?? undefined} style={sx(saveStyle)}>
                  {saving ? 'Saving…' : 'Log outcome'}
                </a>{' '}
                <a href={v.mpFollow} style={sx('display:inline-flex;align-items:center;height:30px;padding:0 12px;border-radius:7px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;color:#22272E;border:1px solid rgba(34,39,46,.2);')}>
                  Draft follow-up
                </a>
              </div>{' '}
              {!!saveErr && <ErrorNote error={saveErr as never} compact />}
              <div style={sx('font-size:14px;color:#54606C;line-height:1.45;')}>
                <span>{v.mpSaveNote}</span> The follow-up opens in your mail app with partner@ on CC, so the facility has one address to reply to, and deals@ on BCC to file it.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
